<template>
  <div class="animated fadeIn">
    <h4 class="mb-3"><i class="icon-pencil mr-2 text-info"></i>Downtime Entry
      <small class="text-muted ml-1" style="font-size:.75rem">Manual log</small>
    </h4>

    <b-row>
      <!-- Entry Form -->
      <b-col md="6" class="mb-4">
        <b-card header="Log a Downtime Event">
          <b-form @submit.prevent="submit">

            <b-form-group label="Service *" label-for="svc">
              <b-form-select id="svc" v-model="form.serviceName" :options="serviceOptions" required />
            </b-form-group>

            <b-form-group label="Status *" label-for="sts">
              <b-form-select id="sts" v-model="form.status" :options="statusOptions" required>
                <template #first>
                  <b-form-select-option value="" disabled>— Select status —</b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>

            <b-form-group label="Response Time (ms)" label-for="rt"
                          description="Leave blank if service was unreachable">
              <b-form-input id="rt" v-model.number="form.responseTimeMs" type="number"
                            min="0" placeholder="e.g. 5000" />
            </b-form-group>

            <b-form-group label="Checked At" label-for="cat"
                          description="Defaults to current time if left blank">
              <b-form-input id="cat" v-model="form.checkedAt" type="datetime-local" />
            </b-form-group>

            <b-form-group label="Message / Notes" label-for="msg">
              <b-form-textarea id="msg" v-model="form.message" rows="3"
                               placeholder="Describe the incident…" />
            </b-form-group>

            <b-button type="submit" variant="primary" :disabled="submitting" block>
              <b-spinner v-if="submitting" small class="mr-1" />
              {{ submitting ? 'Saving…' : 'Save Entry' }}
            </b-button>
          </b-form>
        </b-card>
      </b-col>

      <!-- Status Quick Guide -->
      <b-col md="6" class="mb-4">
        <b-card header="Status Guide" class="mb-3">
          <b-list-group flush>
            <b-list-group-item class="d-flex align-items-center">
              <b-badge variant="success" class="mr-3" style="width:80px;text-align:center">UP</b-badge>
              <span class="text-muted small">Service is responding normally within acceptable latency.</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex align-items-center">
              <b-badge variant="warning" class="mr-3" style="width:80px;text-align:center">DEGRADED</b-badge>
              <span class="text-muted small">Service responds but with high latency or partial errors.</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex align-items-center">
              <b-badge variant="danger" class="mr-3" style="width:80px;text-align:center">DOWN</b-badge>
              <span class="text-muted small">Service is unreachable or returning critical errors.</span>
            </b-list-group-item>
          </b-list-group>
        </b-card>

        <b-alert variant="info" show>
          <i class="icon-info mr-1"></i>
          Manual entries are tagged <strong>MANUAL</strong> and logged with your username for traceability.
        </b-alert>
      </b-col>
    </b-row>

    <!-- Recent Manual Entries -->
    <b-card no-body>
      <b-card-header><i class="icon-list mr-1"></i>Recent Manual Entries</b-card-header>
      <b-table
        :items="recentEntries"
        :fields="fields"
        :busy="loadingRecent"
        responsive striped hover show-empty
        empty-text="No manual entries yet."
        class="mb-0"
      >
        <template #table-busy>
          <div class="text-center py-3"><b-spinner variant="primary" /></div>
        </template>
        <template #cell(status)="d">
          <b-badge :variant="badgeVariant(d.value)">{{ d.value }}</b-badge>
        </template>
        <template #cell(response_time_ms)="d">
          <span v-if="d.value != null">{{ d.value }} ms</span>
          <span v-else class="text-muted">—</span>
        </template>
        <template #cell(checked_at)="d">{{ d.value | fmtdt }}</template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import downtimeService from '@/services/downtimeService'

export default {
  name: 'DowntimeEntry',
  filters: {
    fmtdt: v => v ? new Date(v).toLocaleString() : '—'
  },
  data() {
    return {
      submitting: false,
      loadingRecent: false,
      form: { serviceName: '', status: '', responseTimeMs: null, message: '', checkedAt: '' },
      recentEntries: [],
      serviceOptions: [
        { value: '',                  text: '— Select service —' },
        { value: 'hrm-api',           text: 'HRM API' },
        { value: 'admin-api',         text: 'Admin API' },
        { value: 'billing-service',   text: 'Billing Service' },
      ],
      statusOptions: [
        { value: 'UP',       text: 'UP' },
        { value: 'DOWN',     text: 'DOWN' },
        { value: 'DEGRADED', text: 'DEGRADED' },
      ],
      fields: [
        { key: 'service_name',    label: 'Service',     sortable: true },
        { key: 'status',          label: 'Status',      sortable: true },
        { key: 'response_time_ms',label: 'Response ms' },
        { key: 'reported_by',     label: 'Reported By' },
        { key: 'message',         label: 'Message' },
        { key: 'checked_at',      label: 'Checked At',  sortable: true },
      ]
    }
  },
  created() {
    this.loadRecent()
  },
  methods: {
    submit() {
      if (!this.form.serviceName || !this.form.status) {
        this.$bvToast.toast('Please select service and status.', { title: 'Validation', variant: 'warning', solid: true })
        return
      }
      this.submitting = true
      downtimeService.saveEntry(
        this.form,
        () => {
          this.submitting = false
          this.$bvToast.toast('Downtime entry saved successfully.', { title: 'Saved', variant: 'success', solid: true })
          this.form = { serviceName: '', status: '', responseTimeMs: null, message: '', checkedAt: '' }
          this.loadRecent()
        },
        err => {
          this.submitting = false
          this.$bvToast.toast(err, { title: 'Error', variant: 'danger', solid: true })
        }
      )
    },
    loadRecent() {
      this.loadingRecent = true
      downtimeService.getRecentManual(
        data => { this.recentEntries = data; this.loadingRecent = false },
        ()    => { this.loadingRecent = false }
      )
    },
    badgeVariant(s) {
      return s === 'UP' ? 'success' : s === 'DOWN' ? 'danger' : s === 'DEGRADED' ? 'warning' : 'secondary'
    }
  }
}
</script>
